<template>
    <div class="full-height full-width">
        <l-map
            ref="mapComponent"
            style="height: 100%; width: 100%"
            :zoom="zoom"
            :max-zoom="maxZoom"
            :center="center"
            @ready="onMapReady"
        >
            <l-tile-layer
                :url="tileUrl"
                :attribution="attribution"
                :options="{
                    className: monochrome ? 'monochrome-tiles' : '',
                    maxZoom: 20,
                    maxNativeZoom: 18,
                }"
            ></l-tile-layer>

            <!-- Locations -->
            <l-marker
                v-for="location in displayLocations"
                :key="'loc-' + location.id"
                :lat-lng="[location.latitude, location.longitude]"
                @click="showDetails(location, 'location')"
            >
                <l-icon
                    v-if="location.icon || location.color"
                    :icon-anchor="[16, 32]"
                    :popup-anchor="[0, -32]"
                >
                    <div
                        class="custom-marker"
                        :style="{ color: location.color || 'primary' }"
                    >
                        <q-icon :name="location.icon || 'place'" size="32px" />
                    </div>
                </l-icon>
            </l-marker>

            <!-- Routes -->
            <l-geo-json
                v-for="route in displayRoutes"
                :key="'route-' + route.id"
                :geojson="route.polygons"
                :options="{ color: route.color || '#3388ff' }"
                @click="showDetails(route, 'route')"
            >
                <q-tooltip>{{ route.name }}</q-tooltip>
            </l-geo-json>

            <!-- Congregation Borders -->
            <l-geo-json
                v-for="border in congregationBorders"
                :key="'border-' + border.id"
                :geojson="border.border_geojson"
                :options="{
                    color: '#ff7800',
                    weight: 2,
                    fill: false,
                    interactive: false,
                }"
            >
            </l-geo-json>
        </l-map>

        <!-- Details Dialog -->
        <q-dialog v-model="detailsOpen">
            <q-card style="min-width: 350px">
                <q-card-section class="row items-center q-pb-none">
                    <div class="text-h6">{{ selectedItem?.name }}</div>
                    <q-space />
                    <q-btn icon="close" flat round dense v-close-popup />
                </q-card-section>

                <q-card-section class="q-pt-md">
                    <div
                        v-if="selectedItem?.description"
                        v-html="selectedItem.description"
                    ></div>

                    <div
                        v-if="
                            selectedType === 'location' &&
                            selectedItem?.images?.length
                        "
                        class="q-mt-md"
                    >
                        <div class="row q-col-gutter-sm">
                            <div
                                v-for="(image, index) in selectedItem.images"
                                :key="image.id"
                                class="col-6 col-md-4"
                            >
                                <q-img
                                    :src="
                                        '/karte/bild/' +
                                        selectedItem.id +
                                        '/' +
                                        image.id +
                                        '/small'
                                    "
                                    :ratio="1"
                                    class="cursor-pointer rounded-borders"
                                    @click="showLightbox(index)"
                                >
                                    <div
                                        class="absolute-full flex flex-center bg-transparent transition-show hover-overlay"
                                    >
                                        <q-icon
                                            name="zoom_in"
                                            color="white"
                                            size="sm"
                                        />
                                    </div>
                                </q-img>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="
                            selectedType === 'location' &&
                            selectedItem?.important_hints
                        "
                        class="q-mt-md"
                    >
                        <div class="text-subtitle2 text-negative">
                            Wichtige Hinweise:
                        </div>
                        <div v-html="selectedItem.important_hints"></div>
                    </div>

                    <div
                        v-if="
                            selectedType === 'route' &&
                            selectedItem?.locations?.length
                        "
                        class="q-mt-md"
                    >
                        <div class="text-subtitle2 q-mb-xs">
                            Standorte dieser Route:
                        </div>
                        <q-list dense separator class="rounded-borders">
                            <q-item
                                v-for="loc in selectedItem.locations"
                                :key="loc.id"
                                clickable
                                v-ripple
                                @click="showLocationDetails(loc)"
                            >
                                <q-item-section avatar min-width="24px">
                                    <q-icon
                                        :name="loc.icon || 'place'"
                                        :color="loc.color || 'primary'"
                                    />
                                </q-item-section>
                                <q-item-section>
                                    <q-item-label>{{ loc.name }}</q-item-label>
                                </q-item-section>
                                <q-item-section side>
                                    <q-icon name="chevron_right" size="xs" />
                                </q-item-section>
                            </q-item>
                        </q-list>
                    </div>
                </q-card-section>
            </q-card>
        </q-dialog>

        <!-- Lightbox -->
        <teleport to="body">
            <vue-easy-lightbox
                :visible="lightboxVisible"
                :imgs="lightboxImages"
                :index="lightboxIndex"
                @hide="lightboxVisible = false"
            ></vue-easy-lightbox>
        </teleport>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import {
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LGeoJson,
} from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.locatecontrol/dist/L.Control.Locate.min.css";
import L from "leaflet";
import * as LocateControlModule from "leaflet.locatecontrol";
import VueEasyLightbox from "vue-easy-lightbox";

// Fix missing icon issues in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
    iconRetinaUrl: iconRetinaUrl,
    iconUrl: iconUrl,
    shadowUrl: shadowUrl,
});

const props = defineProps({
    locations: {
        type: Array,
        default: () => [],
    },
    congregationBorders: {
        type: Array,
        default: () => [],
    },
    routes: {
        type: Array,
        default: () => [],
    },
    initialLat: {
        type: Number,
        default: 51.165691,
    },
    initialLng: {
        type: Number,
        default: 10.451526,
    },
    monochrome: {
        type: Boolean,
        default: false,
    },
    filterRouteIds: {
        type: Array,
        default: null,
    },
});

const map = ref(null);
const mapComponent = ref(null);
const zoom = ref(13);
const maxZoom = ref(20);
const center = ref([props.initialLat, props.initialLng]);
const tileUrl = ref("/tiles/{z}/{x}/{y}.png");
const attribution = ref(
    '&copy; <a target="_blank" rel="noopener" href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
);

const detailsOpen = ref(false);
const selectedItem = ref(null);
const selectedType = ref("");

const lightboxVisible = ref(false);
const lightboxIndex = ref(0);

const lightboxImages = computed(() => {
    if (selectedType.value === "location" && selectedItem.value?.images) {
        return selectedItem.value.images.map((img) => ({
            src:
                "/karte/bild/" +
                selectedItem.value.id +
                "/" +
                img.id +
                "/large",
            title: img.title,
            description: img.description,
        }));
    }
    return [];
});

const showLightbox = (index) => {
    lightboxIndex.value = index;
    lightboxVisible.value = true;
};
const displayRoutes = computed(() => {
    if (!props.filterRouteIds || props.filterRouteIds.length === 0) {
        return props.routes;
    }
    return props.routes.filter((r) => props.filterRouteIds.includes(r.id));
});

const displayLocations = computed(() => {
    if (!props.filterRouteIds || props.filterRouteIds.length === 0) {
        return props.locations;
    }
    const locationIds = new Set();
    displayRoutes.value.forEach((r) => {
        if (r.locations) {
            r.locations.forEach((l) => locationIds.add(l.id));
        }
    });
    return props.locations.filter((l) => locationIds.has(l.id));
});

const getRouteLatLngs = (route) => {
    if (route.polygons && Array.isArray(route.polygons)) {
        return route.polygons;
    }
    return [];
};

const showLocationDetails = (loc) => {
    const fullLoc = props.locations.find((l) => l.id === loc.id) || loc;
    showDetails(fullLoc, "location");
};

const showDetails = (item, type) => {
    if (type === "location" && item) {
        const fullLoc = props.locations.find((l) => l.id === item.id);
        selectedItem.value = fullLoc || item;
    } else {
        selectedItem.value = item;
    }
    selectedType.value = type;
    detailsOpen.value = true;
};

const autoCenterMap = (mapInstance) => {
    if (!mapInstance) return;

    const bounds = L.latLngBounds();
    let hasPoints = false;

    displayLocations.value.forEach((loc) => {
        if (loc.latitude && loc.longitude) {
            bounds.extend([
                parseFloat(loc.latitude),
                parseFloat(loc.longitude),
            ]);
            hasPoints = true;
        }
    });

    displayRoutes.value.forEach((route) => {
        if (route.polygons) {
            try {
                const geoJsonLayer = L.geoJSON(route.polygons);
                const routeBounds = geoJsonLayer.getBounds();
                if (routeBounds.isValid()) {
                    bounds.extend(routeBounds);
                    hasPoints = true;
                }
            } catch (e) {
                console.error(
                    "Error calculating bounds for route:",
                    route.id,
                    e,
                );
            }
        }
    });

    if (hasPoints && bounds.isValid()) {
        mapInstance.fitBounds(bounds, { padding: [20, 20], maxZoom: 16 });
    }
};

const onMapReady = (mapInstance) => {
    if (!mapInstance) {
        return;
    }
    map.value = mapInstance;
    autoCenterMap(mapInstance);

    const locateOptions = {
        position: "topright",
        strings: {
            title: window.translations
                ? window.translations["common.map.locate_me"]
                : "Show my location",
        },
        icon: "material-icons",
        iconElementTag: "i",
        iconLoading: "material-icons spin",
        locateOptions: {
            enableHighAccuracy: true,
        },
    };

    try {
        const locateFunc =
            LocateControlModule.locate ||
            (typeof locate === "function" ? locate : null);
        if (typeof locateFunc === "function") {
            locateFunc(locateOptions).addTo(mapInstance);
        } else {
            // Fallback: Ensure L.control.locate exists, as some build environments might not
            // correctly attach it to the imported L object if there are multiple versions
            // or specific bundling configurations.
            const locateControlFunc =
                L.control.locate || (L.Control && L.Control.Locate);

            if (typeof locateControlFunc !== "function") {
                // Fallback: try to check global scope if applicable
                // In some environments, the plugin might attach to a global L
                if (
                    window.L &&
                    typeof window.L.control?.locate === "function"
                ) {
                    window.L.control.locate(locateOptions).addTo(mapInstance);
                    return;
                }
                console.error(
                    "Leaflet LocateControl is not loaded correctly. locate function and L.control.locate are undefined. Module content:",
                    LocateControlModule,
                );
                return;
            }

            if (L.control.locate) {
                L.control.locate(locateOptions).addTo(mapInstance);
            } else {
                new L.Control.Locate(locateOptions).addTo(mapInstance);
            }
        }
    } catch (e) {
        console.error("Error adding locate control:", e);
    }
};

watch(
    [() => displayLocations.value, () => displayRoutes.value],
    () => {
        if (map.value) {
            autoCenterMap(map.value);
        }
    },
    { deep: true },
);

onMounted(() => {
    // Logic here if needed
});
</script>

<style>
.leaflet-control-locate a i {
    font-family: "Material Icons";
    content: "my_location";
    font-style: normal;
}

.leaflet-control-locate a i::before {
    content: "my_location";
}

.monochrome-tiles img {
    filter: grayscale(100%);
}

.custom-marker {
    display: flex;
    justify-content: center;
    align-items: center;
    text-shadow: 0 0 3px #fff;
    width: 32px;
    height: 32px;
}

.transition-show {
    transition: opacity 0.3s;
    opacity: 0;
}

.hover-overlay:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.3);
}
.leaflet-div-icon {
    background: none;
    border-color: transparent;
}

.vel-modal,
.vel-img-modal,
.vel-mask,
.vel-container {
    z-index: 99999 !important;
}
</style>
